import {Component, ErrorInfo, ReactNode} from 'react';

// Route-level safety net. A failed lazy chunk after a deploy (stale index.html holding old
// hashed asset names) reloads once; anything else shows a calm message with a Reload button.
const RELOAD_FLAG = 'chunk-reloaded';

const isChunkError = (error: unknown): boolean =>
    error instanceof Error && /Loading chunk|dynamically imported module|Importing a module script failed/i.test(error.message);

type State = {error: Error | null};

export default class ErrorBoundary extends Component<{children: ReactNode}, State> {
    state: State = {error: null};

    static getDerivedStateFromError(error: Error): State {
        return {error};
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        if (isChunkError(error)) {
            try {
                if (!sessionStorage.getItem(RELOAD_FLAG)) {
                    sessionStorage.setItem(RELOAD_FLAG, '1');
                    window.location.reload();
                    return;
                }
            } catch {
                // ignore
            }
        }
        console.error('Unhandled error', error, info.componentStack);
    }

    componentDidUpdate(_: unknown, prev: State) {
        if (prev.error && !this.state.error) {
            try {
                sessionStorage.removeItem(RELOAD_FLAG);
            } catch {
                // ignore
            }
        }
    }

    render() {
        if (!this.state.error) return this.props.children;
        return (
            <div className="mx-auto flex w-full max-w-[560px] flex-col gap-4 px-4 pt-16">
                <h1 className="title m-0">Something went wrong</h1>
                <p className="m-0 text-ink-2">Nothing you did; the page tripped over itself. Reloading usually sorts it.</p>
                <details className="meta">
                    <summary>Details</summary>
                    <pre className="m-0 mt-2 whitespace-pre-wrap break-words text-xs">{this.state.error.message}</pre>
                </details>
                <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
                    Reload
                </button>
            </div>
        );
    }
}
